import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogComponent } from '@app/components/dialog/dialog.component';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TagDictionaryItem, TagDictionaryService } from '@app/services/tag-dictionary.service';
import { ButtonComponent } from '@app/components/button/button.component';

type TagModalMode = 'create' | 'edit';

@Component({
  selector: 'app-tag-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DialogComponent,
    InputTextModule,
    TextareaModule,
    CheckboxModule,
    ButtonComponent,
  ],
  templateUrl: './tag-modal.component.html',
  styleUrls: ['./tag-modal.component.scss'],
})
export class TagModalComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Output() saved = new EventEmitter<TagDictionaryItem>();

  @Input() mode: TagModalMode = 'create';
  @Input() tag: TagDictionaryItem | null = null;
  @Input() existingTags: TagDictionaryItem[] = [];
  @Input() disableVisibilityControls = false;
  @Input() initialName: string = '';

  form = {
    name: '',
    description: '',
    visible: true,
    isPublic: false,
    comment: '',
  };

  saving = false;
  duplicateError = '';
  private destroy$ = new Subject<void>();

  constructor(
    private readonly tagDictionaryService: TagDictionaryService,
    private readonly translate: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.hydrateForm();
    }

    if (changes['tag'] && this.visible) {
      this.hydrateForm();
    }

    if (changes['initialName'] && this.visible && this.mode === 'create' && this.initialName) {
      this.form.name = this.initialName;
      this.checkDuplicate();
    }
  }

  get isDuplicate(): boolean {
    return !!this.duplicateError;
  }

  get canSave(): boolean {
    return !!this.form.name.trim() && !this.isDuplicate && !this.saving;
  }

  onNameInput(): void {
    this.checkDuplicate();
  }

  save(): void {
    if (!this.canSave) {
      return;
    }

    const payload = {
      name: this.form.name.trim(),
      description: this.form.description?.trim() || undefined,
      visible: this.disableVisibilityControls ? true : this.form.visible,
      isPublic: this.disableVisibilityControls ? false : this.form.isPublic,
      comment: this.form.comment?.trim() || undefined,
    };

    this.saving = true;
    const request$ =
      this.mode === 'edit' && this.tag?.id
        ? this.tagDictionaryService.updateTag({ id: this.tag.id, ...payload })
        : this.tagDictionaryService.createTagDetailed(payload);

    request$
      .pipe(takeUntil(this.destroy$), finalize(() => (this.saving = false)))
      .subscribe({
        next: (tag) => {
          this.saved.emit(tag);
          this.close();
        },
        error: () => {
          this.duplicateError = this.translate.instant('UPLOAD_IMAGES.MODAL_TAGS_SAVE_ERROR') || 'Failed to save tag';
        },
      });
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.reset();
  }

  private hydrateForm(): void {
    const base = this.tag ?? {
      name: this.initialName || '',
      description: '',
      visible: true,
      isPublic: false,
      comment: '',
    };

    this.form = {
      name: this.mode === 'edit' && base.name ? base.name : (this.initialName || base.name || ''),
      description: base.description ?? '',
      visible: this.disableVisibilityControls ? true : base.visible ?? true,
      isPublic: this.disableVisibilityControls ? false : base.isPublic ?? false,
      comment: base.comment ?? '',
    };

    this.checkDuplicate();
  }

  private reset(): void {
    this.form = {
      name: '',
      description: '',
      visible: true,
      isPublic: false,
      comment: '',
    };
    this.duplicateError = '';
  }

  private checkDuplicate(): void {
    const name = this.form.name.trim().toLowerCase();
    if (!name) {
      this.duplicateError = '';
      return;
    }

    const duplicate = this.existingTags.some(
      (tag) => tag.name?.trim().toLowerCase() === name && tag.id !== this.tag?.id,
    );

    this.duplicateError = duplicate ? this.translate.instant('UPLOAD_IMAGES.MODAL_TAGS_DUPLICATE') : '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
